import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { DropFile } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { Button } from "components/DesignSystem/Button/Button";
import { Spinner } from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import {
  AnnotationDoc,
  DocumentAnnotationPanel,
  DocumentPin,
} from "components/DocumentAnnotation/DocumentAnnotationPanel";
import { SidebarQuestion } from "components/DocumentAnnotation/QuestionCard";
import { QuestionSidebar } from "components/DocumentAnnotation/QuestionSidebar";
import { DoubleGreenTick } from "components/icons/DoubleGreenTick";
import { DISPLAY_DATE } from "constants/date";
import { SUBMITTED, REVIEW_PENDING, ACCEPTED } from "constants/sensitiveData";
import dayjs from "dayjs";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { CaretLeft } from "components/icons/CaretLeft";
import FilingInfo from "static/images/FilingInfo.svg";
import {
  getUpdateChatQNAAnswerCacheKey,
  useGetChatQNAQuery,
  useSubmitChatQNAMutation,
  useUpdateChatQNAMutation,
} from "store/apis/chatQnA";
import { ChatQnAResponse } from "types/Models/chatQnA";
import { FileObject } from "types/Models/fileObject";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { ArrayElement } from "types/utils/ArrayElement";
import { AnswerCell } from "./AnswerCell";
import { DocumentUploadCell } from "./DocumentUploadCell";
import { FilePreviewCell } from "./FilePreviewCell";
import { RequestAnswersTable } from "./RequestAnswersTable";
import { taskApis } from "store/apis/task";
import { useDispatch } from "react-redux";
import { useModal, ModalClose } from "hooks/useModal";
import { PreviousResponses } from "./PreviousResponses";
import { AcceptedResponses } from "./AcceptedResponses";
import { AllAnsweredQuestions } from "./AllAnsweredQuestions";
import { MobileReferenceDocuments } from "./MobileReferenceDocuments";

type RequestedAnswer = ArrayElement<ChatQnAResponse["requested_answers"]>;

export const REQUEST_ANSWERS_VARIANT = {
  ANNOTATED: "annotated",
  TABLE: "table",
} as const;
export type RequestAnswersVariant =
  (typeof REQUEST_ANSWERS_VARIANT)[keyof typeof REQUEST_ANSWERS_VARIANT];

type SubmitResponseConfirmModalProps = {
  isOpen: boolean;
  close: ModalClose;
  onConfirm: () => void;
  isSubmitting?: boolean;
};

const SubmitResponseConfirmModal = ({
  isOpen,
  close,
  onConfirm,
  isSubmitting,
}: SubmitResponseConfirmModalProps) => (
  <Modal.Root open={isOpen} onOpenChange={close}>
    <Modal.Content size="small">
      <StatusModal
        type="warning"
        title="Submit response?"
        showCloseButton={false}
      >
        <div className="t-text-body t-text-text-30">
          Once submitted, you won&apos;t be able to make any further edits.
          Please make sure everything is correct before continuing.
        </div>
      </StatusModal>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button type="button" customType="secondary">
            Go back
          </Button>
        </Modal.RawClose>
        <Button
          type="button"
          customType="primary"
          onClick={onConfirm}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Submit response
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  </Modal.Root>
);

export const AddRequestAnswers = ({
  isOpen,
  close,
  requestId,
  groupId,
  variant,
}: ModalProps & {
  requestId: string;
  groupId: string;
  variant?: RequestAnswersVariant;
}) => {
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();

  const { data, isLoading, isFetching, isSuccess } = useGetChatQNAQuery(
    { groupId, requestId },
    { skip: !groupId || !requestId, refetchOnMountOrArgChange: true }
  );

  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [activePinId, setActivePinId] = useState<string | null>(null);
  // Mobile shows one pane at a time: the questions, with the document opened
  // on demand from a question's annotation chip or the documents list.
  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);

  const [submitQuestions, { isLoading: isSubmitting }] =
    useSubmitChatQNAMutation();
  const [, { isLoading: isSavingAnswer }] = useUpdateChatQNAMutation({
    fixedCacheKey: getUpdateChatQNAAnswerCacheKey(requestId),
  });
  const isSyncingAnswers = isSavingAnswer || (isFetching && !isLoading);

  const { isAdmin } = useRoleBasedView();

  const isSubmitted =
    data?.status === SUBMITTED ||
    data?.status === REVIEW_PENDING ||
    data?.status === ACCEPTED;
  const isReviewPending = data?.status === REVIEW_PENDING;
  const isFullyAccepted = data?.status === ACCEPTED;
  const allAnsweredResponses = useMemo(
    () => [
      ...(data?.requested_answers ?? []),
      ...(data?.accepted_responses ?? []),
    ],
    [data?.requested_answers, data?.accepted_responses]
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [scrollToAnswerId, setScrollToAnswerId] = useState<string | null>(null);
  const submitConfirmModal = useModal();

  const isAnswerFilled = useCallback(
    ({ answer, documents, is_prefilled, field_metadata }: RequestedAnswer) => {
      const isRequired = field_metadata ? field_metadata.is_required : true;
      if (!isRequired) return true;

      return (
        is_prefilled ||
        (answer?.trimStart?.()?.length ?? 0) > 0 ||
        (documents?.length ?? 0) > 0
      );
    },
    []
  );

  const errorAnswerIds = hasAttemptedSubmit
    ? (data?.requested_answers ?? [])
        .filter((a) => !isAnswerFilled(a))
        .map((a) => a.uuid)
    : [];

  const isAllAnswered = useMemo(
    () => data?.requested_answers.every(isAnswerFilled),
    [data, isAnswerFilled]
  );

  const isTaskConnected = useMemo(
    () =>
      data?.requested_answers.some((r) => r.field_metadata !== null) ?? false,
    [data]
  );

  const handleAnswerSaved = useCallback(() => setIsDraftSaved(true), []);

  const handleQuestionClick = useCallback(
    (id: string) => {
      const answer = data?.requested_answers.find((a) => a.uuid === id);
      if (answer?.document_index != null) {
        setActiveDocIndex(answer.document_index);
      }
      setActivePinId(id);
      setTimeout(() => setActivePinId(null), 1200);
    },
    [data?.requested_answers]
  );

  const handleViewDocument = useCallback(
    (id: string) => {
      const answer = data?.requested_answers.find((a) => a.uuid === id);
      if (answer?.document_index != null) {
        setActiveDocIndex(answer.document_index);
      }
      setActivePinId(id);
      setIsDocViewerOpen(true);
    },
    [data?.requested_answers]
  );

  const handleOpenReferenceDocument = useCallback((index: number) => {
    setActiveDocIndex(index);
    setIsDocViewerOpen(true);
  }, []);

  const closeDocViewer = useCallback(() => {
    setIsDocViewerOpen(false);
    setActivePinId(null);
  }, []);

  const handleSaveAndClose = () => {
    successToast({ message: "Responses saved" });
  };

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);
    const firstUnanswered = data?.requested_answers.find(
      (a) => !isAnswerFilled(a)
    );
    if (firstUnanswered) {
      setScrollToAnswerId(null);
      requestAnimationFrame(() => setScrollToAnswerId(firstUnanswered.uuid));
      if (firstUnanswered.document_index != null) {
        setActiveDocIndex(firstUnanswered.document_index);
      }
      return;
    }
    setScrollToAnswerId(null);
    submitConfirmModal.open();
  };

  const handleConfirmedSubmit = async () => {
    try {
      if (groupId) {
        await submitQuestions({ requestId, groupId }).unwrap();
        dispatch(taskApis.util.invalidateTags(["Tasks"]));
        submitConfirmModal.close();
        successToast({ message: "Responses submitted" });
        close();
      } else {
        throw new Error("Group ID is not available.");
      }
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message || (error as string),
      });
    }
  };

  const handleTableSubmit = async () => {
    try {
      if (groupId) {
        await submitQuestions({ requestId, groupId }).unwrap();
        dispatch(taskApis.util.invalidateTags(["Tasks"]));
        successToast({ message: "Information saved successfully" });
        close();
      } else {
        throw new Error("Group ID is not available.");
      }
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message || (error as string),
      });
    }
  };

  const annotationDocs: AnnotationDoc[] = useMemo(
    () =>
      (data?.reference_documents ?? []).map((doc) => ({
        id: doc.file_uuid,
        name: doc.name,
        previewUrl: "",
        fileType: doc.file_type ?? "PDF",
      })),
    [data?.reference_documents]
  );

  const pins: DocumentPin[] = useMemo(
    () =>
      (data?.requested_answers ?? []).flatMap((a, index) => {
        if (
          a.document_index == null ||
          a.x_position == null ||
          a.y_position == null
        )
          return [];
        return [
          {
            id: a.uuid,
            docIndex: a.document_index,
            x: a.x_position,
            y: a.y_position,
            pageNumber: a.page_number ?? 1,
            number: index + 1,
            isActive: activePinId === a.uuid,
          },
        ];
      }),
    [data?.requested_answers, activePinId]
  );

  const sidebarQuestions: SidebarQuestion[] = useMemo(
    () =>
      (data?.requested_answers ?? []).map((a) => ({
        id: a.uuid,
        text: a.question,
        docIndex: a.document_index ?? null,
        x: a.x_position ?? null,
        y: a.y_position ?? null,
      })),
    [data?.requested_answers]
  );

  const initialValue = useMemo(
    () =>
      data?.requested_answers.reduce(
        (acc, cv) => ({
          ...acc,
          [cv.uuid]: cv.answer || "",
          [`documents_${cv.uuid}`]: (cv.documents ?? []).map(
            ({ uuid, name, file_type, file_uuid, is_previewable }) => ({
              uuid,
              name,
              file_type: file_type as DropFile["file_type"],
              is_previewable,
              file_uuid,
            })
          ),
        }),
        {} as Record<string, string | (DropFile & { file_uuid: string })[]>
      ) ?? {},
    [data?.requested_answers]
  );

  const hasAutoActivatedFirstPin = useRef(false);

  useEffect(() => {
    if (hasAutoActivatedFirstPin.current || !data?.requested_answers) return;

    const firstPinnedAnswer = data.requested_answers.find(
      (a) => a.document_index != null
    );
    if (!firstPinnedAnswer) return;

    hasAutoActivatedFirstPin.current = true;
    setActiveDocIndex(firstPinnedAnswer.document_index!);
    setActivePinId(firstPinnedAnswer.uuid);
    setTimeout(() => setActivePinId(null), 1200);
  }, [data?.requested_answers]);

  const renderQuestionExtra = useCallback(
    (question: SidebarQuestion) => {
      const answer = data?.requested_answers.find(
        (a) => a.uuid === question.id
      );
      const questionDocuments = answer?.question_documents ?? [];

      return (
        <div className="t-flex t-flex-col t-gap-1">
          {questionDocuments.length > 0 && (
            <div className="t-flex t-flex-col t-gap-1">
              {questionDocuments.map((doc) => (
                <FilePreviewCell
                  key={doc.uuid}
                  file={{
                    ...doc,
                    uuid: doc.file_uuid,
                    file_type: doc.file_type as FileObject["file_type"],
                  }}
                />
              ))}
            </div>
          )}
          <div className="t-mb-1">
            <PreviousResponses rounds={answer?.previous_rounds ?? []} />
          </div>
          <AnswerCell
            uuid={question.id}
            requestId={requestId}
            isSubmitted={isSubmitted}
            groupId={groupId}
            initialAnswer={answer?.answer ?? ""}
            fieldMetadata={answer?.field_metadata ?? null}
            onSave={handleAnswerSaved}
            onFocus={
              question.docIndex != null
                ? () => handleQuestionClick(question.id)
                : undefined
            }
          />
          <DocumentUploadCell
            answerRequestId={question.id}
            groupId={groupId}
            isDisabled={isSubmitted}
            isSubmitted={isSubmitted}
            onUploadSuccess={() => setIsDraftSaved(true)}
          />
        </div>
      );
    },
    [
      data?.requested_answers,
      isSubmitted,
      groupId,
      requestId,
      handleAnswerSaved,
      handleQuestionClick,
    ]
  );

  const resolvedVariant =
    variant ??
    (data?.task_id
      ? REQUEST_ANSWERS_VARIANT.TABLE
      : REQUEST_ANSWERS_VARIANT.ANNOTATED);
  const isTableVariant = resolvedVariant === REQUEST_ANSWERS_VARIANT.TABLE;
  const hasAnnotationDocs = !isTableVariant && annotationDocs.length > 0;
  const showDraftBanner = !isSubmitted && isDraftSaved && !isSyncingAnswers;
  const showFooterLeft = showDraftBanner || isSubmitted || isSyncingAnswers;
  const showNotFinalizedBanner = isAdmin && !isSubmitted;
  const footerButtonClassName = "sm:t-flex-1";

  const notFinalizedBanner = showNotFinalizedBanner && (
    <Banner
      type="warning"
      title="Responses not yet finalized"
      description="These are draft responses and can still be updated by the customer. Verify the information before proceeding with any final work."
    />
  );

  let modalTitle = "Requested Information";
  if (isSubmitted) modalTitle = "Information Submitted";
  else if (data?.is_follow_up) modalTitle = "Follow-up Questions";

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content
        size={isTableVariant ? "fullscreen" : hasAnnotationDocs ? "xxxl" : "xl"}
        className={classNames({ "t-h-[90vh]": hasAnnotationDocs })}
        onEscapeKeyDown={(e) => e.preventDefault()}
        useCustomOverlay={!isTableVariant}
      >
        <Async.Root
          isLoading={isLoading}
          isEmpty={data?.requested_answers.length === 0}
          isSuccess={isSuccess}
        >
          <Async.Empty>
            <Modal.Header>
              <div>
                <Modal.Title>Requested Information</Modal.Title>
              </div>
              <Modal.Close type="button" />
            </Modal.Header>
            <Modal.Body>
              <EmptyScreen text="No Data Found" />
            </Modal.Body>
          </Async.Empty>

          <Async.Success>
            <Modal.Header>
              <div>
                <Modal.Title>{modalTitle}</Modal.Title>
                {isTableVariant ? (
                  <Modal.Subtitle className="sm:t-text-caption">
                    {isAdmin
                      ? "These are draft responses and can still be updated by the customer. Verify the information before proceeding with any final work."
                      : "Please provide the information at your convenience. Your responses are saved as you go, and you may submit them once ready."}
                  </Modal.Subtitle>
                ) : (
                  !isAdmin && (
                    <Modal.Subtitle className="sm:t-text-caption">
                      Please provide the information at your convenience. Your
                      responses are saved as you go, and you may submit them
                      once ready.
                    </Modal.Subtitle>
                  )
                )}
              </div>
              <Modal.Close type="button" />
            </Modal.Header>

            <FormikForm
              initialValues={initialValue}
              onSubmit={() => {}}
              className="t-flex t-flex-col t-flex-1 t-min-h-0"
            >
              <Modal.Body
                className={classNames("t-flex-1 t-min-h-0", {
                  "!t-p-0 !t-overflow-hidden": hasAnnotationDocs,
                  "!t-p-0": !hasAnnotationDocs && !isTableVariant,
                })}
              >
                {isTableVariant ? (
                  <RequestAnswersTable
                    requestedAnswers={data?.requested_answers ?? []}
                    requestId={requestId}
                    groupId={groupId}
                    isSubmitted={isSubmitted}
                    showDocumentsColumn={!isTaskConnected}
                    onAnswerSaved={handleAnswerSaved}
                    onFileUploaded={() => setIsDraftSaved(true)}
                  />
                ) : hasAnnotationDocs ? (
                  <div className="t-relative t-flex t-flex-row t-overflow-hidden t-h-full">
                    {!isSmallScreen && (
                      <div className="t-overflow-auto t-h-full t-flex-1">
                        <DocumentAnnotationPanel
                          docs={annotationDocs}
                          activeDocIndex={activeDocIndex}
                          onTabChange={setActiveDocIndex}
                          pins={pins}
                          onPinClick={handleQuestionClick}
                          groupId={groupId}
                        />
                      </div>
                    )}

                    <div
                      className={classNames(
                        "t-h-full t-overflow-hidden",
                        isSmallScreen
                          ? "t-w-full"
                          : "t-w-[550px] t-shrink-0 t-border-0 t-border-l t-border-solid t-border-neutral-10"
                      )}
                    >
                      {isFullyAccepted ? (
                        <AllAnsweredQuestions
                          responses={allAnsweredResponses}
                        />
                      ) : (
                        <QuestionSidebar
                          questions={sidebarQuestions}
                          activePinId={activePinId}
                          docs={annotationDocs}
                          variant="answer"
                          showHeader={true}
                          onQuestionClick={handleQuestionClick}
                          onViewDocument={
                            isSmallScreen ? handleViewDocument : undefined
                          }
                          renderQuestionExtra={renderQuestionExtra}
                          errorQuestionIds={errorAnswerIds}
                          scrollToQuestionId={scrollToAnswerId}
                          topBanner={
                            <>
                              {notFinalizedBanner}
                              {isSmallScreen && (
                                <MobileReferenceDocuments
                                  docs={annotationDocs}
                                  groupId={groupId}
                                  onOpenDocument={handleOpenReferenceDocument}
                                />
                              )}
                            </>
                          }
                          questionTextClassName="t-text-text-60"
                          bottomContent={
                            <AcceptedResponses
                              responses={data?.accepted_responses ?? []}
                              startNumber={sidebarQuestions.length + 1}
                            />
                          }
                        />
                      )}
                    </div>

                    {isSmallScreen && isDocViewerOpen && (
                      <div className="t-absolute t-inset-0 t-z-10 t-flex t-flex-col t-bg-surface">
                        <div className="t-flex t-h-12 t-shrink-0 t-items-center t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-px-3">
                          <Button
                            type="button"
                            customType="ghost_icon"
                            size="small"
                            onClick={closeDocViewer}
                            aria-label="Back to questions"
                          >
                            <CaretLeft />
                          </Button>
                          <span className="t-text-subtext-sm t-text-text-60">
                            Back to questions
                          </span>
                        </div>
                        <div className="t-min-h-0 t-flex-1 t-overflow-auto">
                          <DocumentAnnotationPanel
                            docs={annotationDocs}
                            activeDocIndex={activeDocIndex}
                            onTabChange={setActiveDocIndex}
                            pins={pins}
                            onPinClick={handleQuestionClick}
                            groupId={groupId}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : isFullyAccepted ? (
                  <AllAnsweredQuestions
                    responses={allAnsweredResponses}
                    showHeader={false}
                  />
                ) : (
                  <QuestionSidebar
                    questions={sidebarQuestions}
                    activePinId={activePinId}
                    docs={annotationDocs}
                    variant="answer"
                    showHeader={false}
                    compact
                    onQuestionClick={handleQuestionClick}
                    renderQuestionExtra={renderQuestionExtra}
                    errorQuestionIds={errorAnswerIds}
                    scrollToQuestionId={scrollToAnswerId}
                    topBanner={notFinalizedBanner}
                    questionTextClassName="t-text-text-60"
                    bottomContent={
                      <AcceptedResponses
                        responses={data?.accepted_responses ?? []}
                        startNumber={sidebarQuestions.length + 1}
                      />
                    }
                  />
                )}
              </Modal.Body>

              <Modal.Footer>
                <div
                  className={classNames(
                    "t-flex t-items-center t-gap-3 sm:t-flex-col sm:t-items-stretch",
                    {
                      "t-justify-between": showFooterLeft,
                      "t-justify-end": !showFooterLeft,
                    }
                  )}
                >
                  {isSubmitted && (
                    <div className="t-text-text-30 t-text-body-sm t-flex t-items-center t-gap-1">
                      <img
                        src={FilingInfo}
                        alt="FilingInfo"
                        className="t-size-3 t-shrink-0"
                      />
                      <div className="t-italic">
                        Information submitted by {data.submitted_by} on{" "}
                        {dayjs(data.submitted_at).format(DISPLAY_DATE)}
                        {isReviewPending && " and is awaiting review"}
                      </div>
                    </div>
                  )}

                  {isSyncingAnswers && !isSubmitted && (
                    <span className="t-flex t-items-center t-gap-2 t-text-text-30 t-text-body-sm">
                      <span className="t-flex t-shrink-0">
                        <Spinner size="small" />
                      </span>
                      Saving your response...
                    </span>
                  )}

                  {showDraftBanner && (
                    <span className="t-flex t-items-center t-gap-2 t-text-text-30 t-text-body-sm">
                      <span className="t-flex t-shrink-0">
                        <DoubleGreenTick />
                      </span>
                      Draft is saved. Submit to share your responses with the
                      team.
                    </span>
                  )}

                  <div className="t-flex t-shrink-0 t-items-center t-justify-end t-gap-3">
                    {isTableVariant ? (
                      <>
                        <Modal.RawClose asChild>
                          <Button
                            type="button"
                            className={footerButtonClassName}
                          >
                            Close
                          </Button>
                        </Modal.RawClose>
                        {!isSubmitted && (
                          <Button
                            customType="primary"
                            onClick={handleTableSubmit}
                            className={footerButtonClassName}
                            disabled={
                              !isAllAnswered || isSubmitting || isSyncingAnswers
                            }
                            isLoading={isSubmitting}
                          >
                            Submit
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Modal.RawClose asChild>
                          <Button
                            type="button"
                            onClick={
                              isSubmitted ? undefined : handleSaveAndClose
                            }
                            className={footerButtonClassName}
                          >
                            {isSubmitted ? "Close" : "Save & Close"}
                          </Button>
                        </Modal.RawClose>
                        {!isSubmitted && (
                          <Button
                            type="button"
                            customType="primary"
                            onClick={handleSubmit}
                            className={footerButtonClassName}
                            disabled={isSubmitting || isSyncingAnswers}
                            isLoading={isSubmitting}
                          >
                            {data?.is_follow_up
                              ? "Submit response"
                              : "Submit responses"}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Modal.Footer>

              {!isTableVariant && (
                <SubmitResponseConfirmModal
                  isOpen={submitConfirmModal.isOpen}
                  close={submitConfirmModal.close}
                  onConfirm={handleConfirmedSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
            </FormikForm>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
